<template>
  <div
    :ref="isSelected ? 'selectedChatItem' : null"
    class="chat-history-item"
    :class="{
      'chat-history-item--hoverable': showActions,
      'chat-history-item--compact': !showActions,
      'chat-history-item--selected': isSelected,
      'chat-history-item--unseen': hasUnseen,
    }"
    @click="handleSelect"
  >
    <div class="chat-icon">
      <i class="fas fa-comment"></i>
    </div>
    <div class="chat-content">
      <div class="chat-title-row">
        <div class="chat-title">{{ chatTitle }}</div>
        <status-spinner
          v-if="isWorking"
          :size="14"
          class="working-spinner"
          title="Working"
        />
        <span v-else-if="hasUnseen" class="unseen-dot" />
      </div>
      <div v-if="showActions && chat.updated_at" class="chat-timestamp">
        {{ formattedTimestamp }}
      </div>
    </div>
    <div v-if="showActions" class="chat-actions">
      <button
        class="action-button action-button--delete"
        @click.stop="handleDelete"
        title="Delete chat"
      >
        <i class="fas fa-trash"></i>
      </button>
    </div>
  </div>
</template>

<script>
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import StatusSpinner from "@/components/OliverBotV2/shared/StatusSpinner.vue";

dayjs.extend(relativeTime);

export default {
  name: "ChatHistoryItem",
  components: { StatusSpinner },
  props: {
    chat: {
      type: Object,
      required: true,
    },
    showActions: {
      type: Boolean,
      default: false,
    },
    selectedChatId: {
      type: [String, Number],
      default: null,
    },
    hasUnseen: {
      type: Boolean,
      default: false,
    },
    /** Oliver is still answering in this conversation. */
    isWorking: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    chatTitle() {
      return this.chat.title || "New Chat";
    },
    formattedTimestamp() {
      if (!this.chat.updated_at) return "";
      return dayjs(this.chat.updated_at).fromNow();
    },
    isSelected() {
      return this.selectedChatId && this.chat.id === this.selectedChatId;
    },
  },
  methods: {
    handleSelect() {
      this.$emit("select", this.chat);
    },
    handleDelete() {
      this.$emit("delete", this.chat);
    },
  },
};
</script>

<style lang="scss" scoped>
.chat-history-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 12px;
  background: transparent;
  border-radius: 12px;
  cursor: pointer;
  transition: background 0.15s ease;
  position: relative;

  &:hover {
    .chat-actions {
      opacity: 1;
      visibility: visible;
    }
  }

  &--hoverable {
    padding-right: 40px;
  }

  &--selected {
    background: var(--oliver-accent-light);

    .chat-icon {
      color: var(--oliver-accent);
    }

    .chat-title {
      color: var(--oliver-accent);
      font-weight: 600;
    }
  }

  &--unseen {
    .chat-title {
      color: var(--oliver-accent);
      font-weight: 700;
    }
  }

  &:active {
    background: var(--oliver-btn-subtle-hover, rgba(0, 0, 0, 0.07));
  }
}

.chat-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--oliver-accent);
  font-size: 14px;
  margin-top: 2px;
  flex-shrink: 0;
}

.chat-content {
  flex: 1;
  min-width: 0;
}

.chat-title-row {
  display: flex;
  align-items: center;
  min-width: 0;
  margin-bottom: 2px;
}

.chat-title {
  font-size: 12px;
  font-weight: 500;
  color: var(--oliver-text);
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  flex: 1;
  min-width: 0;
  line-height: 1.3;
}

.working-spinner {
  margin-left: 6px;
}

.unseen-dot {
  display: inline-block;
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--oliver-accent);
  margin-left: 6px;
}

.chat-timestamp {
  font-size: 10px;
  color: var(--oliver-text-muted);
}

.chat-actions {
  position: absolute;
  right: 10px;
  top: 8px;
  display: flex;
  gap: 4px;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.15s ease, visibility 0.15s ease;
}

.action-button {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: var(--oliver-text-muted);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;

  &:hover {
    background: var(--oliver-btn-subtle-bg, rgba(0, 0, 0, 0.06));
    color: var(--oliver-text);
  }

  &--delete {
    &:hover {
      background: rgba(229, 62, 62, 0.1);
      color: #e53e3e;
    }
  }

  i {
    font-size: 11px;
  }
}
</style>
